import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { RadioButton } from 'primereact/radiobutton';
import axiosInstance from '../config/axios.config';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getReportComponent, determineReportType, reportComponentMap } from '../config/reportTypeMapping';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import InputDataCard from '../components/reports/input/InputDataCard';
import { adminLookupService } from '../services/adminLookupService';
import { useMasking } from '../layout/context/maskingcontext';
import ProtectedContent from '../components/ProtectedContent';

// Use the standard axios instance from config - removed custom createApiClient

const ReportHistory = () => {
    const navigate = useNavigate();
    const reportViewRef = useRef(null);
    const { isMasked } = useMasking();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [showReportView, setShowReportView] = useState(false);
    const [reportDetails, setReportDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [dateRange, setDateRange] = useState(null);
    
    // New state for report selection and lead attachment
    const [selectedReports, setSelectedReports] = useState([]);
    const [showLeadDialog, setShowLeadDialog] = useState(false);
    const [availableLeads, setAvailableLeads] = useState([]);
    const [selectedLead, setSelectedLead] = useState(null);
    const [attachingReports, setAttachingReports] = useState(false);
    const [leadOption, setLeadOption] = useState('existing'); // 'new' or 'existing'
    
    // Search input states (not applied until search button is clicked)
    const [searchInputValue, setSearchInputValue] = useState('');
    const [searchFromDate, setSearchFromDate] = useState(null);
    const [searchToDate, setSearchToDate] = useState(null);
    const [selectedReportType, setSelectedReportType] = useState(null);
    const [selectedAssignmentStatus, setSelectedAssignmentStatus] = useState(null);
    
    // Pagination state for lazy loading
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    
    // Sorting state
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    // Get user ID from localStorage or auth context
    const userId = JSON.parse(localStorage.getItem('user'))?.id || 1;
    
    // Role detection for admin filters
    const userRoles = JSON.parse(localStorage.getItem('userRoles') || '[]');
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const primaryRole = userRoles.length > 0 ? userRoles[0] : (userInfo.user_type || 'user');
    const isTenantAdmin = primaryRole === 'tenant_admin' || primaryRole === 'owner';
    const isCompanyAdmin = primaryRole === 'company_admin' || primaryRole === 'company_owner';
    
    // Admin filter state (minimal add)
    const [selectedCompanyOption, setSelectedCompanyOption] = useState(null);
    const [companySuggestions, setCompanySuggestions] = useState([]);
    const [companyQuery, setCompanyQuery] = useState('');
    const [companyLoading, setCompanyLoading] = useState(false);
    
    const [selectedUserOption, setSelectedUserOption] = useState(null);
    const [userSuggestions, setUserSuggestions] = useState([]);
    const [userQuery, setUserQuery] = useState('');
    const [userLoading, setUserLoading] = useState(false);
    
    // Define assignment status options
    const assignmentStatusOptions = [
        { label: 'All', value: null },
        { label: 'Assigned', value: 'assigned' },
        { label: 'Unassigned', value: 'unassigned' }
    ];
    
    // Define all available report types
    const reportTypes = [
        { label: 'All Reports', value: null },
        { label: 'Equifax', value: 'Equifax' },
        { label: 'Experian', value: 'Experian' },
        { label: 'Employment By Mobile', value: 'Employment By Mobile' },
        { label: 'Employment By UAN', value: 'Employment By UAN' },
        { label: 'Bank Statement Analysis', value: 'Bank Statement Analysis' },
        { label: 'Mobile Bureau Look up', value: 'Mobile Bureau Look up' },
        { label: 'PAN v1', value: 'PAN v1' },
        { label: 'PAN v2', value: 'PAN v2' },
        { label: 'Voter ID v1', value: 'Voter ID v1' },
        { label: 'Voter ID v2', value: 'Voter ID v2' },
        { label: 'CA Membership', value: 'CA Membership' },
        { label: 'Driving Licence v1', value: 'Driving Licence v1' },
        { label: 'Driving Licence v2', value: 'Driving Licence v2' },
        { label: 'RC Details', value: 'RC Details' }
    ];

    useEffect(() => {
        fetchReportHistory();
    }, [first, rows, sortField, sortOrder, selectedReportType, selectedAssignmentStatus]); // Fetch when pagination, sorting or filters change

    // Removed debounced search for lead name since we no longer have leads

    // Admin lookup handlers/effects (minimal)
    const handleCompanyDropdownFocus = async () => {
        if (!isTenantAdmin) return;
        if (companySuggestions.length > 0) return;
        setCompanyLoading(true);
        try {
            const items = await adminLookupService.fetchCompaniesInitial();
            setCompanySuggestions(items);
        } finally {
            setCompanyLoading(false);
        }
    };
    
    useEffect(() => {
        if (!isTenantAdmin) return;
        if (companyQuery && companyQuery.length >= 3) {
            const h = setTimeout(async () => {
                setCompanyLoading(true);
                try {
                    const items = await adminLookupService.searchCompanies(companyQuery);
                    setCompanySuggestions(items);
                } finally {
                    setCompanyLoading(false);
                }
            }, 450);
            return () => clearTimeout(h);
        }
    }, [companyQuery, isTenantAdmin]);

    useEffect(() => {
        if (!(isTenantAdmin || isCompanyAdmin)) return;
        if (userQuery && userQuery.length >= 3) {
            const h = setTimeout(async () => {
                setUserLoading(true);
                try {
                    let items = [];
                    if (isTenantAdmin) {
                        items = await adminLookupService.searchTenantUsers({ q: userQuery, company_id: selectedCompanyOption?.value });
                    } else if (isCompanyAdmin) {
                        items = await adminLookupService.searchCompanyUsers(userQuery);
                    }
                    setUserSuggestions(items);
                } finally {
                    setUserLoading(false);
                }
            }, 450);
            return () => clearTimeout(h);
        }
    }, [userQuery, isTenantAdmin, isCompanyAdmin, selectedCompanyOption]);

    const fetchReportHistory = async (overrideFilters = null, pageOverride = null, rowsOverride = null, clearAll = false) => {
        setLoading(true);
        try {

            // Use override values if provided, otherwise use state values
            const currentRows = rowsOverride || rows;
            const page = pageOverride || Math.floor(first / rows) + 1;

            const params = {
                page: page,
                per_page: currentRows
            };

            // If clearAll is true, skip all filters
            if (!clearAll) {
                // Add sorting parameters
                if (sortField) {
                    params.sort_field = sortField;
                    params.sort_order = sortOrder === 1 ? 'asc' : 'desc';
                }

                // Add report type filter if selected (skip when "All Reports" is selected - null/empty)
                if (selectedReportType && selectedReportType !== '') {
                    params.report_name = selectedReportType;
                }

                // Add assignment status filter if selected (skip when "All" is selected - null/empty)
                if (selectedAssignmentStatus && selectedAssignmentStatus !== '') {
                    params.attachment_status = selectedAssignmentStatus;
                }

                // Use override filters if provided, otherwise use state
                const filters = overrideFilters || {
                    dateRange: dateRange
                };

                // Add date range filter if set
                if (filters.dateRange) {
                    if (filters.dateRange[0]) {
                        params.date_from = filters.dateRange[0].toISOString().split('T')[0];
                    }
                    if (filters.dateRange[1]) {
                        params.date_to = filters.dateRange[1].toISOString().split('T')[0];
                    }
                }

                // Append admin filters (minimal)
                if (isTenantAdmin && selectedCompanyOption?.value) {
                    params.company_Id = selectedCompanyOption.value;
                }
                if ((isTenantAdmin || isCompanyAdmin) && selectedUserOption?.value) {
                    params.user_id = selectedUserOption.value;
                }
            }

            console.log('Fetching with params:', params);
            
            const response = await axiosInstance.get(`/report-history/${userId}`, { params });
            
            if (response.data.success) {
                console.log('Report History - Total reports received:', response.data.data.length);
                
                setReports(response.data.data);
                const pagination = response.data.pagination;
                if (pagination) {
                    setTotalRecords(pagination.total);
                } else {
                    // If no pagination info, assume all records are returned
                    setTotalRecords(response.data.data.length);
                }
            } else {
                toast.error('Failed to fetch report history');
            }
        } catch (error) {
            console.error('Error fetching report history:', error);
            if (error.response && error.response.status === 500) {
                toast.error('Server error. Please ensure you are accessing from a tenant subdomain (e.g., shri.localhost)');
            } else {
                toast.error('Error fetching report history');
            }
        } finally {
            setLoading(false);
        }
    };

    const viewReport = async (report) => {
        // If clicking the same report, toggle view
        if (selectedReport?.report_id === report.report_id && showReportView) {
            setShowReportView(false);
            setSelectedReport(null);
            setReportDetails(null);
            return;
        }
        
        setSelectedReport(report);
        setShowReportView(true);
        setDetailsLoading(true);
        
        try {
            const reportId = report.report_id || report.id; // Use report_id if available, fallback to id
            console.log('Fetching report details for ID:', reportId);
            console.log('Report object:', report);
            const response = await axiosInstance.get(`/report-history/details/${reportId}`);
            console.log('Report details response:', response.data);
            
            if (response.data.success) {
                const reportData = response.data.data;
                console.log('Setting report details:', reportData);
                console.log('Report JSON:', reportData.report_json);
                console.log('Report Name:', reportData.report_name);
                
                // Ensure report_json is available
                if (!reportData.report_json) {
                    console.warn('No report_json in response, checking api_response');
                    if (reportData.api_response) {
                        reportData.report_json = reportData.api_response;
                    }
                }
                
                setReportDetails(reportData);
                // No need to scroll since we're replacing the entire view
            } else {
                console.error('Failed response:', response.data);
                toast.error(response.data.message || 'Failed to fetch report details');
                setShowReportView(false);
            }
        } catch (error) {
            console.error('Error fetching report details:', error);
            console.error('Error response:', error.response?.data);
            toast.error(error.response?.data?.message || 'Error fetching report details');
            setShowReportView(false);
        } finally {
            setDetailsLoading(false);
        }
    };

    const downloadReport = async (report) => {
        console.log('Download Report Called for:', report.report_name, report);
        
        // For Bureau reports (Equifax/Experian), Bank Statement reports, and Mobile Bureau Lookup, use the component's download logic
        if (
            // report.report_name === 'BUREAU_REPORT' || report.report_name === 'Equifax' ||
            report.report_name === 'Bank Statement' || report.report_name === 'BANK_STATEMENT' ||
            report.report_name === 'Bank Statement Analysis' || report.report_name === 'BANK_STATEMENT_ANALYSIS' 
            // report.report_name === 'Mobile Number Lookup' || report.report_name === 'MOBILE_BUREAU_LOOKUP' || 
            // report.report_name === 'Mobile Bureau Lookup' || report.report_name === 'Mobile Bureau Look up' || report.report_name === 'Mobile Lookup'
            ) {
            try {
                // Get report details first to find the proper download URL
                const reportId = report.report_id || report.id;
                const response = await axiosInstance.get(`/report-history/details/${reportId}`);
                
                if (response.data.success) {
                    const reportDetails = response.data.data;
                    
                    // Debug logging for Equifax reports
                    if (report.report_name === 'BUREAU_REPORT' || report.report_name === 'Equifax') {
                        console.log('Equifax Report Details:', reportDetails);
                        console.log('Report JSON:', reportDetails?.report_json);
                        console.log('API Response:', reportDetails?.api_response);
                        
                        // Parse report_json if it's a string
                        if (typeof reportDetails?.report_json === 'string') {
                            try {
                                const parsed = JSON.parse(reportDetails.report_json);
                                console.log('Parsed Equifax report_json:', parsed);
                                console.log('PDF URL in parsed JSON:', parsed?.pdf_download_url || parsed?.pdf_url);
                                reportDetails.report_json = parsed;
                            } catch (e) {
                                console.error('Failed to parse Equifax report_json:', e);
                            }
                        }
                    }
                    
                    // Debug logging for Bank Statement reports
                    if (report.report_name === 'Bank Statement' || 
                        report.report_name === 'BANK_STATEMENT' || 
                        report.report_name === 'Bank Statement Analysis' ||
                        report.report_name === 'BANK_STATEMENT_ANALYSIS') {
                        console.log('Bank Statement Report Details:', reportDetails);
                        console.log('Report JSON:', reportDetails?.report_json);
                        console.log('API Response:', reportDetails?.api_response);
                        
                        // Parse report_json if it's a string
                        if (typeof reportDetails?.report_json === 'string') {
                            try {
                                const parsed = JSON.parse(reportDetails.report_json);
                                console.log('Parsed report_json:', parsed);
                                reportDetails.report_json = parsed;
                            } catch (e) {
                                console.error('Failed to parse report_json:', e);
                            }
                        }
                    }
                    
                    // Debug logging for Mobile Bureau Lookup reports
                    if (report.report_name === 'Mobile Number Lookup' || 
                        report.report_name === 'MOBILE_BUREAU_LOOKUP' || 
                        report.report_name === 'Mobile Bureau Lookup' || 
                        report.report_name === 'Mobile Bureau Look up' ||
                        report.report_name === 'Mobile Lookup') {
                        console.log('=== MOBILE BUREAU DOWNLOAD DEBUG ===');
                        console.log('Mobile Bureau Lookup Report Details:', reportDetails);
                        console.log('PDF URL locations to check:');
                        console.log('- reportDetails.pdf_url:', reportDetails?.pdf_url);
                        console.log('- reportDetails.pdf_download_url:', reportDetails?.pdf_download_url);
                        console.log('- reportDetails.data.pdf_url:', reportDetails?.data?.pdf_url);
                        console.log('- reportDetails.data.pdf_download_url:', reportDetails?.data?.pdf_download_url);
                        console.log('- reportDetails.report_json.pdf_url:', reportDetails?.report_json?.pdf_url);
                        console.log('- reportDetails.report_json.pdf_download_url:', reportDetails?.report_json?.pdf_download_url);
                        console.log('- reportDetails.api_response.pdf_url:', reportDetails?.api_response?.pdf_url);
                        console.log('Report JSON type:', typeof reportDetails?.report_json);
                        console.log('Report JSON raw:', reportDetails?.report_json);
                        console.log('API Response:', reportDetails?.api_response);
                        
                        // Try to parse report_json if it's a string to see what's inside
                        if (typeof reportDetails?.report_json === 'string') {
                            try {
                                const parsed = JSON.parse(reportDetails.report_json);
                                console.log('PARSED report_json:', parsed);
                                console.log('PARSED report_json keys:', Object.keys(parsed));
                                console.log('PARSED pdf_download_url:', parsed?.pdf_download_url);
                                console.log('PARSED pdf_url:', parsed?.pdf_url);
                            } catch (e) {
                                console.error('Failed to parse report_json:', e);
                            }
                        }
                        
                        // Parse report_json if it's a string
                        if (typeof reportDetails?.report_json === 'string') {
                            try {
                                const parsed = JSON.parse(reportDetails.report_json);
                                console.log('Parsed report_json:', parsed);
                                console.log('Parsed report_json.pdf_url:', parsed?.pdf_url);
                                reportDetails.report_json = parsed;
                            } catch (e) {
                                console.error('Failed to parse report_json:', e);
                            }
                        }
                    }
                    
                    // Check for download URL in multiple possible locations
                    // NOTE: report_json might have been parsed above, so check it first
                    let downloadUrl = reportDetails?.report_json?.pdf_download_url ||  // Check parsed JSON first (Equifax)
                                      reportDetails?.report_json?.pdf_url ||          // Check parsed JSON pdf_url
                                      reportDetails?.pdf_url ||                        // Mobile Bureau Lookup uses this
                                      reportDetails?.pdf_download_url || 
                                      reportDetails?.download_link || 
                                      reportDetails?.download_url ||
                                      reportDetails?.data?.pdf_url ||                  // Check data.pdf_url too
                                      reportDetails?.data?.pdf_download_url ||
                                      reportDetails?.data?.download_link ||
                                      reportDetails?.report_json?.download_link ||
                                      reportDetails?.report_json?.download_url ||
                                      reportDetails?.api_response?.pdf_url ||          // Check api_response.pdf_url
                                      reportDetails?.api_response?.pdf_download_url ||
                                      reportDetails?.api_response?.download_link ||
                                      report?.download_url;
                    
                    // Special handling for Mobile Bureau Lookup - parse report_json if it's a string
                    if (!downloadUrl && (report.report_name === 'Mobile Number Lookup' || 
                                         report.report_name === 'MOBILE_BUREAU_LOOKUP' || 
                                         report.report_name === 'Mobile Bureau Lookup' || 
                                         report.report_name === 'Mobile Bureau Look up' ||
                                         report.report_name === 'Mobile Lookup')) {
                        let mobileReportJson = reportDetails?.report_json;
                        if (typeof mobileReportJson === 'string') {
                            try {
                                mobileReportJson = JSON.parse(mobileReportJson);
                            } catch (e) {
                                console.error('Failed to parse Mobile Bureau report_json:', e);
                            }
                        }
                        downloadUrl = mobileReportJson?.pdf_download_url || mobileReportJson?.pdf_url;
                        console.log('Mobile Bureau - parsed report_json downloadUrl:', downloadUrl);
                        console.log('Mobile Bureau - mobileReportJson keys:', Object.keys(mobileReportJson || {}));
                        console.log('Mobile Bureau - mobileReportJson.pdf_download_url:', mobileReportJson?.pdf_download_url);
                        console.log('Mobile Bureau - mobileReportJson.pdf_url:', mobileReportJson?.pdf_url);
                    }
                    
                    console.log('=== FINAL DOWNLOAD URL FOR MOBILE BUREAU ===');
                    console.log('Final downloadUrl selected:', downloadUrl);
                    console.log('=== END MOBILE BUREAU DEBUG ===');
                    
                    // For Bank Statement reports, also check excel_links
                    if (!downloadUrl && (report.report_name === 'Bank Statement' || 
                                         report.report_name === 'BANK_STATEMENT' || 
                                         report.report_name === 'Bank Statement Analysis' ||
                                         report.report_name === 'BANK_STATEMENT_ANALYSIS')) {
                        // Parse report_json if it's a string (in case not already parsed above)
                        let parsedReportJson = reportDetails?.report_json;
                        if (typeof parsedReportJson === 'string') {
                            try {
                                parsedReportJson = JSON.parse(parsedReportJson);
                            } catch (e) {
                                console.error('Failed to parse report_json for excel links:', e);
                            }
                        }
                        
                        // Check for excel links in various possible locations
                        const excelLinks = reportDetails?.excel_links || 
                                          reportDetails?.data?.excel_links ||
                                          parsedReportJson?.excel_links ||
                                          parsedReportJson?.data?.excel_links ||
                                          reportDetails?.api_response?.excel_links;
                        
                        console.log('Looking for excel_links:', {
                            'reportDetails.excel_links': reportDetails?.excel_links,
                            'reportDetails.data.excel_links': reportDetails?.data?.excel_links,
                            'parsedReportJson.excel_links': parsedReportJson?.excel_links,
                            'parsedReportJson.data.excel_links': parsedReportJson?.data?.excel_links
                        });
                        
                        if (excelLinks && typeof excelLinks === 'object') {
                            // Get the first available excel link
                            const firstKey = Object.keys(excelLinks)[0];
                            if (firstKey && excelLinks[firstKey]?.url) {
                                downloadUrl = excelLinks[firstKey].url;
                                console.log('Using excel link for download:', downloadUrl);
                            }
                        }
                        
                        // Also check if there's a direct download_url in the view_data from the table
                        if (!downloadUrl && report.download_url) {
                            downloadUrl = report.download_url;
                            console.log('Using report.download_url from table data:', downloadUrl);
                        }
                    }
                    
                    if (!downloadUrl) {
                        toast.error('PDF download URL not available');
                        return;
                    }

                    console.log('Download URL found:', downloadUrl);
                    toast.info('Preparing PDF download...');
                    
                    // Special handling for Mobile Bureau and Bank Statement - use full URL directly
                    if (downloadUrl.startsWith('http') && (report.report_name === 'Mobile Number Lookup' || 
                                                           report.report_name === 'MOBILE_BUREAU_LOOKUP' || 
                                                           report.report_name === 'Mobile Bureau Lookup' || 
                                                           report.report_name === 'Mobile Bureau Look up' ||
                                                           report.report_name === 'Mobile Lookup' ||
                                                           report.report_name === 'Bank Statement Analysis' ||
                                                           report.report_name === 'BANK_STATEMENT_ANALYSIS' ||
                                                           report.report_name === 'Bank Statement' ||
                                                           report.report_name === 'BANK_STATEMENT')) {
                        console.log('Opening PDF/Excel directly:', downloadUrl);
                        window.open(downloadUrl, '_blank');
                        toast.success('Download started');
                        return;
                    }
                    
                    // If it's a full URL, extract the API path and use axios
                    if (downloadUrl.startsWith('http')) {
                        try {
                            const url = new URL(downloadUrl);
                            let apiPath = url.pathname;
                            
                            // Ensure the path starts with /api
                            if (!apiPath.startsWith('/api')) {
                                apiPath = `/api${apiPath.startsWith('/') ? '' : '/'}${apiPath}`;
                            }
                            
                            console.log('Extracting API path from URL:', apiPath);
                            
                            const response = await axiosInstance.get(apiPath, {
                                responseType: 'blob',
                                headers: {
                                    'Accept': 'application/pdf',
                                }
                            });
                            
                            const blob = new Blob([response.data], { type: 'application/pdf' });
                            const blobUrl = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.download = `${report.report_name?.replace(/\s+/g, '_') || 'report'}_${report.report_id || Date.now()}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(blobUrl);
                            
                            toast.success('PDF downloaded successfully');
                            return;
                        } catch (error) {
                            console.error('Failed to download PDF via axios:', error);
                            // Fallback to direct browser download
                            const link = document.createElement('a');
                            link.href = downloadUrl;
                            link.download = `${report.report_name?.replace(/\s+/g, '_') || 'report'}_${report.report_id || Date.now()}.pdf`;
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            toast.warning('Download attempted via browser');
                            return;
                        }
                    }
                    
                    // For relative URLs, use the proper axios instance with correct base URL handling
                    try {
                        // Determine if it's Excel or PDF based on URL
                        const isExcel = downloadUrl.includes('.xlsx') || downloadUrl.includes('.xls') || 
                                       downloadUrl.includes('excel') || 
                                       (report.report_name === 'Bank Statement' || report.report_name === 'BANK_STATEMENT' ||
                                        report.report_name === 'Bank Statement Analysis' || report.report_name === 'BANK_STATEMENT_ANALYSIS');
                        
                        const acceptHeader = isExcel ? 
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel' : 
                            'application/pdf';
                        
                        const downloadResponse = await axiosInstance.get(downloadUrl, {
                            responseType: 'blob',
                            headers: {
                                'Accept': acceptHeader,
                            }
                        });

                        const mimeType = isExcel ? 
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 
                            'application/pdf';
                        const fileExtension = isExcel ? '.xlsx' : '.pdf';
                        
                        const blob = new Blob([downloadResponse.data], { type: mimeType });
                        const downloadObjectUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = downloadObjectUrl;
                        // Use appropriate filename based on report type
                        let reportPrefix = 'report';
                        if (report.report_name === 'EXPERIAN_BUREAU_REPORT' || report.report_name === 'Experian') {
                            reportPrefix = 'experian';
                        } else if (report.report_name === 'BUREAU_REPORT' || report.report_name === 'Equifax') {
                            reportPrefix = 'equifax';
                        } else if (report.report_name === 'Bank Statement' || report.report_name === 'BANK_STATEMENT' || 
                                   report.report_name === 'Bank Statement Analysis' || report.report_name === 'BANK_STATEMENT_ANALYSIS') {
                            reportPrefix = 'bank_statement';
                        } else if (report.report_name === 'Mobile Number Lookup' || report.report_name === 'MOBILE_BUREAU_LOOKUP' || 
                                   report.report_name === 'Mobile Bureau Lookup' || report.report_name === 'Mobile Bureau Look up' ||
                                   report.report_name === 'Mobile Lookup') {
                            reportPrefix = 'mobile_bureau_lookup';
                        }
                        link.download = `${reportPrefix}_report_${new Date().getTime()}${fileExtension}`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(downloadObjectUrl);

                        toast.success('PDF downloaded successfully');
                    } catch (downloadError) {
                        console.error('Blob download failed, trying direct URL:', downloadError);
                        // If blob download fails, try opening the URL directly
                        const fullUrl = downloadUrl.startsWith('/') ? 
                            `${window.location.protocol}//${window.location.host}${downloadUrl}` : 
                            downloadUrl;
                        window.open(fullUrl, '_blank');
                        toast.success('Download started');
                    }
                } else {
                    toast.error('Failed to fetch report details for download');
                }
            } catch (error) {
                console.error('Download error:', error);
                let errorMessage = 'Failed to download PDF';
                if (error.response?.status === 500) {
                    errorMessage = 'Server error while generating PDF. Please try again.';
                } else if (error.response?.status === 404) {
                    errorMessage = 'Report not found or data is missing.';
                }
                toast.error(errorMessage);
            }
        } else {
            // For other report types (including Experian with tokenized URLs)
            if (report.download_url) {
                // If URL contains token parameter, it's a public tokenized URL - open directly
                if (report.download_url.includes('?token=')) {
                    console.log('Opening tokenized URL directly:', report.download_url);
                    window.open(report.download_url, '_blank');
                    toast.success('Download started');
                } else {
                    // For non-tokenized URLs, open directly
                    window.open(report.download_url, '_blank');
                    toast.success('Download started');
                }
            } else {
                console.warn('No download URL available for report:', report);
                toast.info('Download not available for this report type');
            }
        }
    };

    // Bank Statement Excel Download Function
    const downloadBankStatementExcel = async (report) => {
        console.log('Download Bank Statement Excel Called for:', report.report_name, report);
        
        try {
            // Get report details first to find the excel links
            const reportId = report.report_id || report.id;
            const response = await axiosInstance.get(`/report-history/details/${reportId}`);
            
            if (response.data.success) {
                const reportDetails = response.data.data;
                console.log('Bank Statement Report Details for Excel:', reportDetails);
                
                // Parse report_json if it's a string
                let parsedReportJson = reportDetails?.report_json;
                if (typeof parsedReportJson === 'string') {
                    try {
                        parsedReportJson = JSON.parse(parsedReportJson);
                        console.log('Parsed report_json for Excel:', parsedReportJson);
                    } catch (e) {
                        console.error('Failed to parse report_json for excel:', e);
                    }
                }
                
                // Check for excel links in various possible locations
                const excelLinks = reportDetails?.excel_links || 
                                  reportDetails?.data?.excel_links ||
                                  parsedReportJson?.excel_links ||
                                  parsedReportJson?.data?.excel_links ||
                                  reportDetails?.api_response?.excel_links;
                
                console.log('Excel links found:', excelLinks);
                
                let downloadUrl = null;
                
                if (excelLinks && typeof excelLinks === 'object') {
                    // Get the first available excel link
                    const firstKey = Object.keys(excelLinks)[0];
                    if (firstKey && excelLinks[firstKey]?.url) {
                        downloadUrl = excelLinks[firstKey].url;
                        console.log('Using excel link for download:', downloadUrl);
                    }
                }
                
                // Also check if there's a direct excel URL
                if (!downloadUrl) {
                    downloadUrl = reportDetails?.excel_url || 
                                 reportDetails?.data?.excel_url ||
                                 parsedReportJson?.excel_url ||
                                 parsedReportJson?.data?.excel_url;
                    console.log('Using direct excel_url:', downloadUrl);
                }
                
                if (!downloadUrl) {
                    toast.error('Excel download URL not available for this report');
                    return;
                }

                console.log('Excel Download URL found:', downloadUrl);
                toast.info('Preparing Excel download...');
                
                // If it's a full URL, open it directly
                if (downloadUrl.startsWith('http')) {
                    window.open(downloadUrl, '_blank');
                    toast.success('Excel download started');
                    return;
                }
                
                // For relative URLs, use the proper axios instance
                try {
                    const downloadResponse = await axiosInstance.get(downloadUrl, {
                        responseType: 'blob',
                        headers: {
                            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
                        }
                    });

                    const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                    const fileExtension = '.xlsx';
                    
                    const blob = new Blob([downloadResponse.data], { type: mimeType });
                    const downloadObjectUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = downloadObjectUrl;
                    link.download = `bank_statement_report_${new Date().getTime()}${fileExtension}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(downloadObjectUrl);

                    toast.success('Excel downloaded successfully');
                } catch (downloadError) {
                    console.error('Excel blob download failed, trying direct URL:', downloadError);
                    // If blob download fails, try opening the URL directly
                    const fullUrl = downloadUrl.startsWith('/') ? 
                        `${window.location.protocol}//${window.location.host}${downloadUrl}` : 
                        downloadUrl;
                    window.open(fullUrl, '_blank');
                    toast.success('Excel download started');
                }
            } else {
                toast.error('Failed to fetch report details for Excel download');
            }
        } catch (error) {
            console.error('Excel download error:', error);
            let errorMessage = 'Failed to download Excel file';
            if (error.response?.status === 500) {
                errorMessage = 'Server error while generating Excel. Please try again.';
            } else if (error.response?.status === 404) {
                errorMessage = 'Excel file not found or data is missing.';
            }
            toast.error(errorMessage);
        }
    };

    const onSearchInputChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
    };

    const handleSearch = () => {
        // Apply all search filters at once
        const newFilters = {
            dateRange: [searchFromDate, searchToDate]
        };
        
        setDateRange([searchFromDate, searchToDate]);
        setFirst(0); // Reset to first page
        
        // Pass the new filters directly to avoid state update delay
        fetchReportHistory(newFilters);
    };
    
    const handleClearFilters = () => {
        // Clear all filters and reset sorting
        setSearchInputValue('');
        setSearchFromDate(null);
        setSearchToDate(null);
        setGlobalFilterValue('');
        setDateRange(null);
        setFirst(0);
        setSortField(null);
        setSortOrder(null);
        setSelectedReportType(null);
        setSelectedAssignmentStatus(null);

        // Clear admin filters (company and user)
        setSelectedCompanyOption(null);
        setCompanyQuery('');
        setCompanySuggestions([]);
        setSelectedUserOption(null);
        setUserQuery('');
        setUserSuggestions([]);

        // Fetch with clearAll flag to bypass all filters from state
        fetchReportHistory(null, null, null, true);

        toast.info('All filters cleared');
    };

    // Report selection functions
    const onReportSelectionChange = (e) => {
        setSelectedReports(e.value);
    };

    const handleAddToLead = () => {
        if (selectedReports.length === 0) {
            toast.warning('Please select at least one report');
            return;
        }
        
        // Filter only unassigned reports
        const unassignedReports = selectedReports.filter(report => 
            report.attachment_status === 'unassigned'
        );
        
        if (unassignedReports.length === 0) {
            toast.warning('Selected reports are already assigned to leads');
            return;
        }
        
        console.log('Selected reports for lead attachment:', unassignedReports);
        setSelectedReports(unassignedReports);
        fetchAvailableLeads();
        setShowLeadDialog(true);
    };

    const fetchAvailableLeads = async () => {
        try {
            // Use the new endpoint that returns both own leads and shared leads with edit/full access
            const response = await axiosInstance.get('/leads/for-attachment');

            if (response.data.success) {
                console.log('Available leads for attachment:', response.data.data);
                // Check what fields are available in the first lead
                if (response.data.data.length > 0) {
                    console.log('First lead fields:', Object.keys(response.data.data[0]));
                    console.log('First lead data:', response.data.data[0]);
                }
                setAvailableLeads(response.data.data);
            } else {
                toast.error('Failed to fetch leads');
            }
        } catch (error) {
            console.error('Error fetching leads:', error);
            toast.error('Error fetching leads');
        }
    };

    const handleAttachReports = async () => {
        if (!selectedLead || selectedReports.length === 0) {
            toast.warning('Please select a lead and reports');
            return;
        }

        setAttachingReports(true);
        try {
            const reportIds = selectedReports.map(report => report.report_id);

            // Prepare request data - include share_id for shared leads
            const requestData = {
                report_ids: reportIds.map(id => parseInt(id)), // Ensure all IDs are integers
                lead_id: parseInt(selectedLead.id) // Ensure lead_id is integer
            };

            // If it's a shared lead, include the share_id
            if (selectedLead.source === 'shared' && selectedLead.share_id) {
                requestData.share_id = parseInt(selectedLead.share_id);
            }

            console.log('Attaching reports data:', {
                ...requestData,
                selectedReports: selectedReports,
                selectedLead: selectedLead,
                isSharedLead: selectedLead.source === 'shared'
            });

            const response = await axiosInstance.post('/report-history/attach-to-lead', requestData);

            if (response.data.success) {
                const leadType = selectedLead.source === 'shared' ? 'shared lead' : 'lead';
                toast.success(`Successfully attached ${response.data.data.attached_reports} reports to ${leadType}`);
                setSelectedReports([]);
                setSelectedLead(null);
                setShowLeadDialog(false);
                // Refresh the report list
                fetchReportHistory();
            } else {
                toast.error(response.data.message || 'Failed to attach reports');
            }
        } catch (error) {
            console.error('Error attaching reports:', error);
            toast.error(error.response?.data?.message || 'Error attaching reports to lead');
        } finally {
            setAttachingReports(false);
        }
    };

    // Column Templates
    const reportNameTemplate = (rowData) => {
        // Display the exact report name from database without any mapping
        return <span>{rowData.report_name}</span>;
    };

    const fetchedDateTemplate = (rowData) => {
        // Parse date in d-m-Y H:i:s format from backend
        if (!rowData.fetched_date) return 'N/A';
        
        // Split date and time
        const [datePart, timePart] = rowData.fetched_date.split(' ');
        if (!datePart) return 'Invalid Date';
        
        // Parse date components
        const [day, month, year] = datePart.split('-');
        
        // Create proper date string that JavaScript can parse
        const dateStr = `${year}-${month}-${day} ${timePart || '00:00:00'}`;
        const date = new Date(dateStr);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
            return rowData.fetched_date; // Return original if parsing fails
        }
        
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const actionTemplate = (rowData) => {
        const isBankStatement = rowData.report_name === 'Bank Statement' || 
                               rowData.report_name === 'BANK_STATEMENT' || 
                               rowData.report_name === 'Bank Statement Analysis' ||
                               rowData.report_name === 'BANK_STATEMENT_ANALYSIS';
        
        return (
            <div className="flex align-items-center gap-1">
                <Button
                    label="View"
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-text p-button-sm"
                    severity="info"
                    onClick={() => viewReport(rowData)}
                    tooltip={selectedReport?.report_id === rowData.report_id && showReportView ? "Hide Report" : "View Report"}
                    tooltipOptions={{ position: 'top' }}
                />
                {!isMasked && (
                    <Button
                        label="Download"
                        icon="pi pi-download"
                        className="p-button-rounded p-button-text p-button-sm"
                        severity="success"
                        onClick={() => downloadReport(rowData)}
                        disabled={false}
                        tooltip="Download Report"
                        tooltipOptions={{ position: 'top' }}
                    />
                )}
                {/* {isBankStatement && (
                    <Button
                        label="Download Excel"
                        icon="pi pi-file-excel"
                        className="p-button-rounded p-button-text p-button-sm"
                        severity="help"
                        onClick={() => downloadBankStatementExcel(rowData)}
                        tooltip="Download Excel Report"
                        tooltipOptions={{ position: 'top' }}
                    />
                )} */}
            </div>
        );
    };

    const handleReportTypeChange = (e) => {
        setSelectedReportType(e.value);
        setFirst(0); // Reset to first page when filter changes
        
        if (e.value) {
            toast.info(`Filtering by: ${e.value}`);
        } else {
            toast.info('Showing all reports');
        }
    };

    const handleAssignmentStatusChange = (e) => {
        setSelectedAssignmentStatus(e.value);
        setFirst(0); // Reset to first page when filter changes
        
        if (e.value) {
            toast.info(`Filtering by: ${e.value === 'assigned' ? 'Assigned Reports' : 'Unassigned Reports'}`);
        } else {
            toast.info('Showing all reports');
        }
    };

    const searchBarTemplate = () => {
        const hasTenant = isTenantAdmin;
        const hasCompany = isCompanyAdmin;
        const hasAdminFilters = hasTenant || hasCompany;
        const smallClass = 'p-inputtext-sm';

        return (
            <div className="surface-ground p-3 border-round mb-4">
                <div className="flex flex-column md:flex-row gap-3 align-items-center">
                    {hasTenant && (
                        <div className={`w-12 md:flex-1`}>
                            {/* <label className="sr-only">Company</label> */}
                            <AutoComplete
                                value={companyQuery || selectedCompanyOption}
                                suggestions={companySuggestions}
                                completeMethod={async ({ query }) => {
                                    setCompanyQuery(query);
                                    if (!query || query.length < 3) {
                                        // Don't clear suggestions if we have a selected value
                                        if (!selectedCompanyOption) {
                                            setCompanySuggestions([]);
                                        }
                                        return;
                                    }
                                    const items = await adminLookupService.searchCompanies(query);
                                    setCompanySuggestions(items);
                                }}
                                onChange={(e) => {
                                    if (typeof e.value === 'string') {
                                        setCompanyQuery(e.value);
                                    } else {
                                        setSelectedCompanyOption(e.value);
                                        setCompanyQuery('');
                                        setSelectedUserOption(null);
                                        setFirst(0);
                                        // Keep selected company in suggestions for forceSelection
                                        if (e.value) {
                                            setCompanySuggestions([e.value]);
                                        }
                                        // Removed auto-fetch - user must click Apply Filters button
                                    }
                                }}
                                onClear={() => {
                                    setSelectedCompanyOption(null);
                                    setCompanyQuery('');
                                    setCompanySuggestions([]);
                                    setSelectedUserOption(null); // Also clear user when company is cleared
                                    // No auto-fetch - user must click Apply Filters button
                                }}
                                field="label"
                                minLength={3}
                                placeholder="Select company"
                                className="w-full p-inputwrapper p-dropdown p-component"
                                panelClassName="admin-ac-panel"
                                inputClassName="p-inputtext-sm"
                                itemTemplate={(item) => <div>{item.label}</div>}
                                forceSelection={false}
                            />
                        </div>
                    )}

                    {(hasTenant || hasCompany) && (
                        <div className={`w-12 md:flex-1`}>
                            {/* <label className="sr-only">User</label> */}
                            <AutoComplete
                                value={userQuery || selectedUserOption}
                                suggestions={userSuggestions}
                                completeMethod={async ({ query }) => {
                                    setUserQuery(query);
                                    if (!query || query.length < 3) {
                                        // Don't clear suggestions if we have a selected value
                                        if (!selectedUserOption) {
                                            setUserSuggestions([]);
                                        }
                                        return;
                                    }
                                    const params = { q: query };
                                    if (isTenantAdmin && selectedCompanyOption?.value) params.company_id = selectedCompanyOption.value;
                                    const items = await adminLookupService.searchUsers(params);
                                    setUserSuggestions(items);
                                }}
                                onChange={(e) => {
                                    if (typeof e.value === 'string') {
                                        setUserQuery(e.value);
                                    } else {
                                        setSelectedUserOption(e.value);
                                        setUserQuery('');
                                        setFirst(0);
                                        // Keep selected user in suggestions for forceSelection
                                        if (e.value) {
                                            setUserSuggestions([e.value]);
                                        }
                                        // Removed auto-fetch - user must click Apply Filters button
                                    }
                                }}
                                onClear={() => {
                                    setSelectedUserOption(null);
                                    setUserQuery('');
                                    setUserSuggestions([]);
                                    // No auto-fetch - user must click Apply Filters button
                                }}
                                field="label"
                                minLength={3}
                                placeholder="user name or email"
                                className="w-full p-inputwrapper p-dropdown p-component"
                                panelClassName="admin-ac-panel"
                                inputClassName="p-inputtext-sm"
                                itemTemplate={(item) => <div>{item.label}</div>}
                                forceSelection={false}
                            />
                        </div>
                    )}

                    <div className={`w-12 md:flex-1`}>
                        <Dropdown
                            value={selectedReportType}
                            options={reportTypes}
                            onChange={handleReportTypeChange}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Search by Report Type"
                            showClear
                            filter
                            filterBy="label"
                            className={`w-full p-dropdown p-component ${smallClass}`}
                            panelClassName="admin-ac-panel"
                            dropdownIcon="pi pi-chevron-down"
                            emptyMessage="No report types found"
                            emptyFilterMessage="No matching report types"
                        />
                    </div>

                    <div className={`w-12 md:flex-1`}>
                        <Dropdown
                            value={selectedAssignmentStatus}
                            options={assignmentStatusOptions}
                            onChange={handleAssignmentStatusChange}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Filter by Status"
                            showClear
                            className={`w-full p-dropdown p-component ${smallClass}`}
                            panelClassName="admin-ac-panel"
                            dropdownIcon="pi pi-chevron-down"
                        />
                    </div>

                    <div className={`w-12 md:flex-1`}>
                        <Calendar
                            value={searchFromDate}
                            onChange={(e) => {
                                setSearchFromDate(e.value);
                                setDateRange([e.value, searchToDate]);
                                setFirst(0);
                                // Removed auto-fetch - user must click Apply Filters button
                            }}
                            dateFormat="dd/mm/yy"
                            placeholder="From Date"
                            showIcon
                            className="w-full"
                            inputClassName={smallClass}
                            maxDate={searchToDate || new Date()}
                        />
                    </div>

                    <div className={`w-12 md:flex-1`}>
                        <Calendar
                            value={searchToDate}
                            onChange={(e) => {
                                setSearchToDate(e.value);
                                setDateRange([searchFromDate, e.value]);
                                setFirst(0);
                                // Removed auto-fetch - user must click Apply Filters button
                            }}
                            dateFormat="dd/mm/yy"
                            placeholder="To Date"
                            showIcon
                            className="w-full"
                            inputClassName={smallClass}
                            minDate={searchFromDate}
                            maxDate={new Date()}
                        />
                    </div>

                    <div className={`w-12 md:flex-1`}>
                        <Button
                            label="Apply Filters"
                            icon="pi pi-check"
                            onClick={() => {
                                console.log('Apply Filters clicked');
                                console.log('selectedCompanyOption:', selectedCompanyOption);
                                console.log('selectedUserOption:', selectedUserOption);
                                console.log('dateRange:', dateRange);
                                setFirst(0);
                                fetchReportHistory();
                            }}
                            severity="primary"
                            className="w-full p-button-sm"
                        />
                    </div>

                    <div className={`w-12 md:flex-1`}>
                        <Button
                            label="Clear All"
                            icon="pi pi-filter-slash"
                            onClick={handleClearFilters}
                            severity="secondary"
                            outlined
                            className="w-full p-button-sm"
                        />
                    </div>
                </div>
            </div>
        );
    };
    const renderViewContent = () => {
        if (detailsLoading) {
            return (
                <div className="flex align-items-center justify-content-center flex-column p-5">
                    <ProgressSpinner />
                    <p className="text-600 mt-3">Loading report details...</p>
                </div>
            );
        }

        if (!reportDetails) {
            return <Message severity="error" text="No details available" />;
        }
        
        console.log('Report Details:', reportDetails);
        console.log('Selected Report:', selectedReport);
        console.log('Selected Report Name:', selectedReport?.report_name);
        console.log('Raw report_json:', reportDetails.report_json);
        
        // Parse report_json if it's a string
        let parsedReportData = null;
        try {
            parsedReportData = typeof reportDetails.report_json === "string" 
                ? JSON.parse(reportDetails.report_json) 
                : reportDetails.report_json;
            console.log('Parsed Report Data:', parsedReportData);
        } catch (error) {
            console.error('Error parsing report_json:', error);
            parsedReportData = reportDetails.report_json;
        }

        // If no parsed data, fall back to other sources
        if (!parsedReportData) {
            parsedReportData = reportDetails.api_response || reportDetails.data || reportDetails;
        }

        // Determine report type dynamically
        let reportType = selectedReport?.report_name;
        console.log('Initial Report Type from selectedReport:', reportType);
        
        // COMPATIBILITY: Handle both correct and incorrect backend responses
        // Check if this is actually a CA Membership report (regardless of what backend says)
        const hasMembershipNumber = selectedReport?.view_data?.status?.input?.membership_no || 
                                   parsedReportData?.status?.input?.membership_no ||
                                   reportDetails?.status?.input?.membership_no;
        
        const hasCaMembershipUrl = selectedReport?.download_url?.includes('/ca-membership/') ||
                                   selectedReport?.download_url?.includes('/tartan/ca-membership/');
        
        const hasCaMembershipData = parsedReportData?.response?.AssociateYear ||
                                    parsedReportData?.data?.response?.AssociateYear ||
                                    selectedReport?.view_data?.AssociateYear;
        
        // If any CA Membership indicators are present, set the correct report type
        if (hasMembershipNumber || hasCaMembershipUrl || hasCaMembershipData) {
            if (reportType !== 'CA Membership' && reportType !== 'CA_MEMBERSHIP_VERIFICATION') {
                console.log(`Correcting report type from "${reportType}" to "CA Membership"`);
                console.log('Detection criteria:', {
                    hasMembershipNumber,
                    hasCaMembershipUrl, 
                    hasCaMembershipData
                });
            }
            reportType = 'CA Membership';
        }
        
        // Special logging for RC_DETAILS
        if (reportType === 'RC_DETAILS' || reportType === 'RC Details') {
            console.log('=== RC DETAILS REPORT DETECTED ===');
            console.log('Report Name:', reportType);
            console.log('Full Selected Report:', selectedReport);
            console.log('Report Details:', reportDetails);
            console.log('Parsed Report Data:', parsedReportData);
        }
        
        // Map database report names to component names
        const reportNameMapping = {
            'EMPLOYMENT_HISTORY': 'Employment History by Mobile',
            'UAN_EMPLOYMENT_HISTORY': 'Employment History by UAN',
            'BUREAU_REPORT': 'Equifax',
            'EXPERIAN_BUREAU_REPORT': 'Experian',
            'BANK_STATEMENT': 'Bank Statement Analysis',
            'PAN_VERIFICATION': 'PAN Details',
            'UAN_VERIFICATION': 'UAN',
            'VOTER_ID_VERIFICATION': 'Voter ID',
            'VOTER_ID_DETAILS': 'Voter ID',
            'DRIVING_LICENCE': 'Driving Licence',
            'DRIVING_LICENCE_VERIFICATION': 'Driving Licence',
            'DRIVING_LICENCE_V2': 'DL2',
            'DRIVING_LICENCE_VERIFICATION_V2': 'DL2',
            'DL_DETAILS': 'DL2',
            'CA_MEMBERSHIP_VERIFICATION': 'CA Membership',
            'MOBILE_BUREAU_LOOKUP': 'Mobile Number Lookup',
            'RC_DETAILS': 'RC Details'
        }; 
        
        // PRIORITY: Use the database report_name mapping first if available
        // This ensures BUREAU_REPORT -> Equifax and EXPERIAN_BUREAU_REPORT -> Experian
        if (reportNameMapping[reportType]) {
            const originalType = reportType;
            reportType = reportNameMapping[reportType];
            console.log('Mapped Report Type from DB name:', originalType, '->', reportType);
            
            // Special logging for RC_DETAILS mapping
            if (originalType === 'RC_DETAILS') {
                console.log('=== RC_DETAILS SUCCESSFULLY MAPPED TO RC Details ===');
            }
        } else if (!reportType) {
            // Only determine from data if we don't have a report_name
            reportType = determineReportType(parsedReportData);
            console.log('Determined Report Type from data:', reportType);
            
            // Additional check for PAN reports if still unknown
            if (reportType === 'Unknown' && parsedReportData) {
                if (parsedReportData.data?.response?.pan || 
                    parsedReportData.response?.pan ||
                    parsedReportData.data?.response?.panNumber ||
                    parsedReportData.data?.response?.pancard_number ||
                    (selectedReport?.report_name && 
                     (selectedReport.report_name.toUpperCase().includes('PAN') ||
                      selectedReport.report_name === 'PAN_VERIFICATION'))) {
                    reportType = 'PAN Details';
                    console.log('Detected PAN report from additional checks');
                }
            }
        }
        
        // Get initial component configuration - try both the mapped type and the original report name
        let componentConfig = getReportComponent(reportType);
        console.log('Getting component for report type:', reportType);
        console.log('Component config found:', componentConfig);
        
        // Special check for RC Details
        if (reportType === 'RC Details') {
            console.log('=== LOOKING FOR RC Details COMPONENT ===');
            console.log('Component exists?', !!componentConfig.component);
            if (!componentConfig.component) {
                console.log('RC Details component not found! Checking reportComponentMap...');
                // Let's check what's available in the map
                const availableTypes = Object.keys(reportComponentMap);
                console.log('Available report types in map:', availableTypes);
                console.log('Does map include "RC Details"?', availableTypes.includes('RC Details'));
                console.log('Does map include "RC_DETAILS"?', availableTypes.includes('RC_DETAILS'));
            }
        }
        
        // If not found with mapped type, try with original report name
        if (!componentConfig.component && selectedReport?.report_name) {
            console.log('Component not found with mapped type, trying original name:', selectedReport.report_name);
            componentConfig = getReportComponent(selectedReport.report_name);
            if (componentConfig.component) {
                console.log('Found component with original report name:', selectedReport.report_name);
                reportType = selectedReport.report_name;
            }
        }
        
        // Final fallback - check if it's actually Employment History by looking at the data
        if (reportType === 'Unknown' || !componentConfig.component) {
            // Check various possible structures for employment data
            const hasEmploymentData = 
                parsedReportData?.response?.employment_data ||
                parsedReportData?.response?.response?.employment_data ||
                parsedReportData?.employment_data ||
                (typeof parsedReportData === 'object' && 
                 JSON.stringify(parsedReportData).includes('employment_data'));
                 
            if (hasEmploymentData || selectedReport?.report_name === 'Employment History by Mobile') {
                reportType = 'Employment History by Mobile';
                console.log('Forced to Employment History based on data structure');
                // Re-get component configuration with the corrected type
                componentConfig = getReportComponent(reportType);
            }
        }
        console.log('Final Report Type:', reportType);
        console.log('Component Config:', componentConfig);
        console.log('Has Component:', !!componentConfig.component);
        
        // Don't render if no parsed data
        if (!parsedReportData) {
            return <Message severity="warn" text="No report data available to display" />;
        }
        
        if (!componentConfig.component) {
            return (
                <>
                    <Message severity="info" text={`Report component not configured for type: ${reportType}`} />
                    <div className="mt-3">
                        <pre className="p-3 surface-100 border-round" style={{ maxHeight: '400px', overflow: 'auto' }}>
                            {JSON.stringify(parsedReportData, null, 2)}
                        </pre>
                    </div>
                </>
            );
        }
        
        // Get component and create enhanced props with parsed data
        const Component = componentConfig.component;
        
        // Create enhanced reportDetails object with parsed data
        const enhancedReportDetails = {
            ...reportDetails,
            report_json: parsedReportData,
            api_response: parsedReportData,
            data: parsedReportData
        };
        
        const componentProps = componentConfig.props(enhancedReportDetails, selectedReport);

        // Override reportData prop to pass the complete report structure with parsed JSON
        // BUT keep the PDF-related props that might have been set by the mapping
        // ALWAYS check the root reportDetails for PDF fields
        const pdfDownloadUrl = reportDetails?.pdf_download_url || componentProps.pdfDownloadUrl || reportDetails?.pdf_url;
        const pdfPasswordHint = reportDetails?.pdf_password_hint || componentProps.pdfPasswordHint;

        componentProps.reportData = enhancedReportDetails;

        // Re-add PDF props after overriding reportData - ALWAYS pass them
        componentProps.pdfDownloadUrl = pdfDownloadUrl || reportDetails?.pdf_download_url || reportDetails?.pdf_url;
        componentProps.pdfPasswordHint = pdfPasswordHint || reportDetails?.pdf_password_hint;

        console.log('Component Props:', componentProps);
        console.log('PDF URL passed to component:', componentProps.pdfDownloadUrl);
        console.log('PDF Hint passed to component:', componentProps.pdfPasswordHint);
        console.log('Report Details Structure:', {
            'reportDetails.pdf_download_url': reportDetails?.pdf_download_url,
            'reportDetails.pdf_url': reportDetails?.pdf_url,
            'reportDetails.id': reportDetails?.id,
            'Full reportDetails': reportDetails
        });
        
        // Handle download success
        const originalDownloadHandlers = {};
        if (componentProps.onDownloadPDF) {
            originalDownloadHandlers.onDownloadPDF = componentProps.onDownloadPDF;
            componentProps.onDownloadPDF = () => {
                const result = originalDownloadHandlers.onDownloadPDF();
                if (result) toast.success('Download started');
                else toast.error('Download URL not available');
            };
        }
        if (componentProps.onDownloadExcel) {
            originalDownloadHandlers.onDownloadExcel = componentProps.onDownloadExcel;
            componentProps.onDownloadExcel = () => {
                const result = originalDownloadHandlers.onDownloadExcel();
                if (result) toast.success('Download started');
                else toast.error('Download URL not available');
            };
        }
        
        // Conditionally render the component only if we have parsed data
        return parsedReportData && (
            <div className="report-history-view">
                {/* Input Data Card */}
                <InputDataCard
                    inputData={selectedReport?.input_data}
                    inputSummary={selectedReport?.input_summary}
                    reportType={reportType}
                    submittedAt={selectedReport?.input_submitted_at}
                    defaultExpanded={true}
                    showExpandButton={true}
                />

                {/* Report Output Component */}
                <Component {...componentProps} />
            </div>
        );
    };

    return (
        <div className="grid">
            <h1 className="sr-only">Report History</h1>
            <style>
                {`
                    /* Hide remaining credits when viewing reports in Report History */
                    .report-history-view span:has(> strong):has(~ span),
                    .report-history-view span:contains("Remaining Credits"),
                    .report-history-view span[style*="border: 2px solid #10b981"] {
                        display: none !important;
                    }
                    
                    /* Alternative approach - hide by targeting the specific credits container */
                    .report-history-view h3 > span[style*="background: linear-gradient"] {
                        display: none !important;
                    }
                    
                    /* Ensure sort icons are visible */
                    .p-datatable .p-sortable-column .p-sortable-column-icon {
                        display: inline-block !important;
                        margin-left: 0.5rem;
                    }
                    
                    .p-datatable .p-sortable-column:hover {
                        background-color: rgba(0, 0, 0, 0.03);
                        cursor: pointer;
                    }
                    
                    .p-datatable .p-sortable-column.p-highlight {
                        background-color: rgba(0, 0, 0, 0.05);
                    }
                    
                    /* Style for report type dropdown */
                    .report-type-dropdown .p-dropdown-item {
                        padding: 0.75rem 1rem;
                    }
                    
                    .report-type-dropdown .p-dropdown-item:hover {
                        background-color: rgba(59, 130, 246, 0.1);
                    }
                    
                    .report-type-dropdown .p-dropdown-item.p-highlight {
                        background-color: rgba(59, 130, 246, 0.15);
                    }
                `}
            </style>
            <div className="col-12">
                {/* Show full report view when a report is selected */}
                {showReportView && selectedReport ? (
                    <>
                        {/* Compact navigation bar outside the card */}
                        <div className="mb-2">
                            <Button
                                icon="pi pi-arrow-left"
                                label="Back to Report History"
                                onClick={() => {
                                    setShowReportView(false);
                                    setSelectedReport(null);
                                    setReportDetails(null);
                                }}
                                severity="secondary"
                                size="small"
                                text
                                className="p-button-sm"
                                style={{ 
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.875rem'
                                }}
                            />
                        </div>
                        
                        {/* Report content card */}
                        <Card ref={reportViewRef} className="p-3">
                            <ProtectedContent
                                type="report"
                                title={selectedReport?.report_name}
                            >
                            <Divider className="my-2" />
                            {renderViewContent()}
                            </ProtectedContent>
                        </Card>
                    </>
                ) : (
                    /* Show report history table when no report is selected */
                    <Card title="Report History">
                        {searchBarTemplate()}
                    
                    <div className="mb-3 flex justify-content-between align-items-center">
                        <div>
                            <Button
                                label="Add to Lead"
                                icon="pi pi-plus"
                                onClick={handleAddToLead}
                                disabled={selectedReports.length === 0}
                                className="p-button-success mr-2"
                            />
                            <span className="text-sm text-gray-600 ml-2">
                                {selectedReports.length} report(s) selected
                            </span>
                        </div>
                        {/* <div className="text-sm text-gray-600">
                            <i className="pi pi-info-circle mr-1"></i>
                            Click on column headers to sort
                        </div> */}
                    </div>
                    
                    <DataTable
                        key={`report-table-${isMasked}`}
                        value={reports}
                        lazy
                        paginator
                        first={first}
                        rows={rows}
                        totalRecords={totalRecords}
                        onPage={(e) => {
                            console.log('Page change:', e);
                            setFirst(e.first);
                            setRows(e.rows);
                            // useEffect will handle the fetch
                        }}
                        onSort={(e) => {
                            console.log('Sort event:', e);
                            setSortField(e.sortField);
                            setSortOrder(e.sortOrder);
                            setFirst(0); // Reset to first page when sorting changes
                        }}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        sortMode="single"
                        removableSort
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                        rowsPerPageOptions={[10, 20]}
                        dataKey="report_id"
                        loading={loading}
                        emptyMessage="No reports found"
                        responsiveLayout="scroll"
                        scrollable
                        scrollHeight="flex"
                        stripedRows
                        size="small"
                        selection={selectedReports}
                        onSelectionChange={onReportSelectionChange}
                        selectionMode="multiple"
                        showGridlines
                    >
                        <Column selectionMode="multiple" headerStyle={{width: '3rem'}} />
                        <Column
                            field="sl_no"
                            header="Sl. No"
                            sortable
                            style={{ minWidth: '80px', textAlign: 'center' }}
                            bodyStyle={{ textAlign: 'center' }}
                        />
                        <Column
                            field="report_name"
                            header="Report Name"
                            body={reportNameTemplate}
                            sortable
                            style={{ minWidth: '200px' }}
                        />
                        <Column
                            field="report_id"
                            header="Report ID"
                            sortable
                            style={{ minWidth: '100px' }}
                        />
                        <Column
                            field="lead_id"
                            header="Lead ID"
                            body={(rowData) => rowData.lead_id ? `#${rowData.lead_id}` : '-'}
                            sortable
                            style={{ minWidth: '100px' }}
                        />
                        <Column
                            field="lead_name"
                            header="Lead Name"
                            body={(rowData) => rowData.lead_name || '-'}
                            sortable
                            style={{ minWidth: '150px' }}
                        />
                        <Column
                            field="attachment_status"
                            header="Status"
                            body={(rowData) => (
                                <span>{rowData.attachment_status === 'unassigned' ? 'Unassigned' : 'Assigned'}</span>
                            )}
                            sortable
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            field="fetched_date"
                            header="Date"
                            body={fetchedDateTemplate}
                            sortable
                            style={{ minWidth: '150px' }}
                        />
                        <Column
                            header="Actions"
                            body={actionTemplate}
                            exportable={false}
                            alignHeader="center"
                            bodyClassName="text-center"
                        />
                    </DataTable>
                    </Card>
                )}
            </div>
            
            {/* Lead Selection Dialog */}
            <Dialog
                header="Add Reports to Lead"
                visible={showLeadDialog}
                onHide={() => {
                    setShowLeadDialog(false);
                    setLeadOption('existing');
                    setSelectedLead(null);
                }}
                style={{ width: '60vw' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button 
                            label="Cancel" 
                            icon="pi pi-times" 
                            onClick={() => {
                                setShowLeadDialog(false);
                                setLeadOption('existing');
                                setSelectedLead(null);
                            }}
                            className="p-button-text"
                            disabled={attachingReports}
                        />
                        <Button 
                            label={leadOption === 'new' ? 'Create New Lead' : 'Attach to Lead'}
                            icon={leadOption === 'new' ? 'pi pi-plus' : 'pi pi-link'}
                            onClick={() => {
                                if (leadOption === 'new') {
                                    // Navigate to Create Lead page with selected reports
                                    navigate('/app/leads/create', { 
                                        state: { 
                                            selectedReports: selectedReports,
                                            fromReportHistory: true 
                                        } 
                                    });
                                } else {
                                    // Attach to existing lead
                                    if (!selectedLead) {
                                        toast.warning('Please select a lead');
                                        return;
                                    }
                                    handleAttachReports();
                                }
                            }}
                            loading={attachingReports}
                            className="p-button-success"
                            disabled={leadOption === 'existing' && !selectedLead}
                        />
                    </div>
                }
            >
                <div className="p-fluid">
                    <div className="field mb-4">
                        <p className="text-sm text-gray-600 mb-3">
                            You are about to attach <strong>{selectedReports.length} report(s)</strong> to a lead:
                        </p>
                        <ul className="list-disc pl-5 mb-4">
                            {selectedReports.map(report => (
                                <li key={report.report_id} className="text-sm">
                                    {report.report_name} (ID: {report.report_id})
                                </li>
                            ))}
                        </ul>
                    </div>
                    
                    {/* Show dropdown only when existing lead is selected */}
                    {leadOption === 'existing' && (
                        <div className="field">
                            <label htmlFor="leadSelect" className="font-medium mb-2 block">
                                Select Lead:
                            </label>
                            <Dropdown
                                id="leadSelect"
                                value={selectedLead}
                                options={availableLeads}
                                onChange={(e) => setSelectedLead(e.value)}
                                optionLabel={(lead) => {
                                    // Extract all three key fields
                                    const mobile = lead.phone || '';

                                    // Get name from various possible fields - backend sends full_name
                                    let first_name = '';
                                    if (lead.full_name) {
                                        first_name = lead.full_name;
                                    } else if (lead.name) {
                                        first_name = lead.name;
                                    } else if (lead.first_name || lead.last_name) {
                                        first_name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
                                    } else if (lead.lead_name) {
                                        first_name = lead.lead_name;
                                    } else {
                                        first_name = 'No Name';
                                    }

                                    const email = lead.email || 'No Email';

                                    // Format: "Mobile | Name | Email"
                                    const parts = [];
                                    if (mobile) parts.push(`mobile: ${mobile}`);
                                    if (first_name) parts.push(`name: ${first_name}`);
                                    if (email && email !== 'No Email') parts.push(`email: ${email}`);

                                    // If we have at least one field, join them
                                    let label = parts.length > 0 ? parts.join(' | ') : `Lead #${lead.id || 'Unknown'}`;

                                    // Add shared indicator for shared leads
                                    if (lead.source === 'shared') {
                                        label = `[Shared by ${lead.shared_by}] ${label}`;
                                    }

                                    return label;
                                }}
                                itemTemplate={(lead) => {
                                    // Custom item template for better visual distinction
                                    const mobile = lead.phone || '';
                                    let name = lead.full_name || lead.name || 'No Name';
                                    const email = lead.email || '';

                                    return (
                                        <div className="flex align-items-center gap-2">
                                            {lead.source === 'shared' && (
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 border-round">
                                                    Shared by {lead.shared_by}
                                                </span>
                                            )}
                                            <div className="flex flex-column">
                                                <span className="font-medium">{name}</span>
                                                <span className="text-sm text-500">
                                                    {mobile && `${mobile}`}
                                                    {mobile && email && ' | '}
                                                    {email}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                }}
                                placeholder="Choose a lead to attach reports"
                                filter
                                showClear
                                className="w-full"
                                emptyMessage="No leads found. Please create a lead first."
                            />
                        </div>
                    )}
                    
                    {/* Show info when new lead is selected */}
                    {leadOption === 'new' && (
                        <div className="field">
                            <Message 
                                severity="info" 
                                text="You will be redirected to the Create Lead page with the selected reports." 
                            />
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
};

export default ReportHistory;